import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, AlertTriangle, ArrowUpRight, Loader2, Tag } from "lucide-react";
import { jobsApi, invoicesApi, estimatesApi, pricebookApi } from "../../lib/api";
import { computeJobProfit, serviceComputedCost, profitOf } from "../../lib/jobCost";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function completedMs(job) {
  const d = job.workCompletedAt || job.end || job.statusChangedAt || null;
  if (!d) return null;
  const t = new Date(d).getTime();
  return isFinite(t) ? t : null;
}

export default function ProfitabilityWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      jobsApi.list().catch(() => []),
      invoicesApi.list().catch(() => []),
      estimatesApi.list().catch(() => []),
      pricebookApi.list().catch(() => []),
    ]).then(([j, i, e, p]) => {
      if (!cancelled) setData({ jobs: j, invoices: i, estimates: e, pricebook: p });
    });
    return () => { cancelled = true; };
  }, []);

  const model = useMemo(() => {
    if (!data) return null;
    const { jobs, invoices, estimates, pricebook } = data;
    const byId = {};
    pricebook.forEach((p) => { byId[p.id] = p; });
    const invByJob = {}; invoices.forEach((i) => { if (i.jobId) invByJob[i.jobId] = i; });
    const estByJob = {}; estimates.forEach((e) => { if (e.jobId) estByJob[e.jobId] = e; });

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

    const rows = jobs
      .filter((j) => j.status === "Completed")
      .filter((j) => { const ms = completedMs(j); return ms !== null && ms >= monthStart; })
      .map((j) => {
        const doc = invByJob[j.id] || estByJob[j.id] || estimates.find((e) => e.id === j.estimateId) || null;
        return { job: j, p: computeJobProfit(j, doc, byId) };
      })
      .filter((r) => r.p.hasData);

    const totalCharged = rows.reduce((s, r) => s + r.p.charged, 0);
    const totalProfit = rows.reduce((s, r) => s + r.p.actualProfit, 0);
    const avgMargin = totalCharged > 0 ? (totalProfit / totalCharged) * 100 : 0;
    const overBudget = rows.filter((r) => r.p.marginDelta < 0).length;
    const worstJobs = [...rows].sort((a, b) => a.p.actualMargin - b.p.actualMargin).slice(0, 4);

    // Lowest-margin services (config level — strategic repricing watchlist)
    const worstServices = pricebook
      .filter((p) => p.kind === "service" && p.isActive !== false && (Number(p.unitPrice) || 0) > 0)
      .map((p) => {
        const cost = serviceComputedCost(p, byId);
        const { pct } = profitOf(p.unitPrice, cost);
        return { item: p, cost, margin: pct };
      })
      .sort((a, b) => a.margin - b.margin)
      .slice(0, 4);

    return { count: rows.length, totalProfit, avgMargin, overBudget, worstJobs, worstServices };
  }, [data]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="dashboard-profitability-widget">
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-7 w-7 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center"><TrendingUp className="h-4 w-4" /></span>
          <div>
            <h3 className="text-[14px] font-semibold text-slate-900">Profitability</h3>
            <p className="text-[11.5px] text-slate-400">This month · completed jobs</p>
          </div>
        </div>
        <button
          onClick={() => navigate("/reports?tab=profitability")}
          data-testid="profit-widget-view-report"
          className="text-[12px] font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          Full report <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {!model ? (
        <div className="p-8 flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="p-5 space-y-4">
          {/* KPIs */}
          <div className="grid grid-cols-3 gap-3">
            <Mini label="Avg margin" value={`${model.avgMargin.toFixed(1)}%`} accent={model.avgMargin >= 0 ? "text-emerald-600" : "text-rose-600"} testid="profit-widget-avg-margin" />
            <Mini label="Total profit" value={money(model.totalProfit)} accent={model.totalProfit >= 0 ? "text-slate-900" : "text-rose-600"} testid="profit-widget-total-profit" />
            <Mini label="Over budget" value={`${model.overBudget}/${model.count}`} accent={model.overBudget > 0 ? "text-amber-600" : "text-slate-900"} testid="profit-widget-over-budget" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Lowest-margin jobs */}
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-2">Lowest-margin jobs</div>
              {model.worstJobs.length === 0 ? (
                <div className="text-[12.5px] text-slate-400 italic" data-testid="profit-widget-no-jobs">No completed jobs this month yet.</div>
              ) : (
                <div className="space-y-1.5">
                  {model.worstJobs.map(({ job, p }) => {
                    const below = p.marginDelta < 0;
                    return (
                      <div key={job.id} className="flex items-center gap-2 text-[12.5px]" data-testid={`profit-widget-job-${job.id}`}>
                        <span className="font-semibold text-slate-700 w-16 shrink-0">{job.id}</span>
                        <span className="text-slate-500 truncate flex-1">{job.customer || "—"}</span>
                        <span className={`font-semibold ${p.actualProfit >= 0 ? "text-slate-700" : "text-rose-600"}`}>{money(p.actualProfit)}</span>
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10.5px] font-semibold w-16 justify-center ${below ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
                          {below ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}{p.actualMargin.toFixed(0)}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Lowest-margin services (reprice watchlist) */}
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-2 flex items-center gap-1">
                <Tag className="h-3 w-3" /> Reprice watchlist
              </div>
              {model.worstServices.length === 0 ? (
                <div className="text-[12.5px] text-slate-400 italic" data-testid="profit-widget-no-services">No priced services yet.</div>
              ) : (
                <div className="space-y-1.5">
                  {model.worstServices.map(({ item, margin }) => {
                    const low = margin < 30;
                    return (
                      <button
                        key={item.id}
                        onClick={() => navigate("/pricebook")}
                        data-testid={`profit-widget-service-${item.id}`}
                        className="w-full flex items-center gap-2 text-[12.5px] hover:bg-slate-50 rounded px-1 py-0.5 -mx-1"
                      >
                        <span className="text-slate-600 truncate flex-1 text-left">{item.name}</span>
                        <span className="text-slate-400">{money(item.unitPrice)}</span>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10.5px] font-semibold w-12 justify-center ${low ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                          {margin.toFixed(0)}%
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {model.overBudget > 0 && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span><strong>{model.overBudget}</strong> job{model.overBudget === 1 ? "" : "s"} came in below the expected margin this month — likely labor or material overruns. Open the full report to see which.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Mini({ label, value, accent, testid }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2.5" data-testid={testid}>
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">{label}</div>
      <div className={`text-[19px] font-bold mt-0.5 ${accent}`}>{value}</div>
    </div>
  );
}
